import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styles from "./AboutLilach.module.css";

import portrait from "../../images/לילך מורד תדמית 5.webp";

gsap.registerPlugin(ScrollTrigger);

const WHATSAPP_NUMBER = "972505864422";
const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("היי לילך, הגעתי מהאתר שלך ואשמח שנדבר")}`;

/* Decorative divider */
const Divider = () => (
  <div className={styles.divider} data-about-divider>
    <span className={styles.dividerLine} />
    <svg className={styles.dividerIcon} viewBox="0 0 24 24" fill="none">
      <path d="M12 2L14.5 9.5H22L16 14L18.5 22L12 17L5.5 22L8 14L2 9.5H9.5L12 2Z" stroke="currentColor" strokeWidth="0.8" />
    </svg>
    <span className={styles.dividerLine} />
  </div>
);

const AboutLilach = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const el = sectionRef.current;
      if (!el) return;

      /* Image parallax */
      gsap.to("[data-about-parallax]", {
        yPercent: -8,
        ease: "none",
        scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: 0.5 },
      });

      /* Watermark slide */
      gsap.fromTo("[data-about-watermark]",
        { xPercent: 8, opacity: 0 },
        {
          xPercent: 0, opacity: 1, duration: 1.4, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 70%", once: true },
        }
      );

      /* Text blocks stagger */
      el.querySelectorAll("[data-about-text]").forEach((block, i) => {
        gsap.fromTo(block,
          { opacity: 0, y: 36 },
          {
            opacity: 1, y: 0, duration: 0.9, ease: "power3.out", delay: i * 0.06,
            scrollTrigger: { trigger: block, start: "top 86%", once: true },
          }
        );
      });

      /* Dividers */
      el.querySelectorAll("[data-about-divider]").forEach((d) => {
        gsap.fromTo(d, { opacity: 0, scale: 0.5 }, {
          opacity: 1, scale: 1, duration: 0.7, ease: "back.out(1.4)",
          scrollTrigger: { trigger: d, start: "top 88%", once: true },
        });
      });

      /* CTA */
      const cta = el.querySelector("[data-about-cta]");
      if (cta) {
        gsap.fromTo(cta, { opacity: 0, y: 24 }, {
          opacity: 1, y: 0, duration: 0.8, ease: "power3.out",
          scrollTrigger: { trigger: cta, start: "top 90%", once: true },
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className={styles.section} id="about">
      <div className={styles.watermark} data-about-watermark>LILACH</div>

      {/* Full-height diagonal image */}
      <div className={styles.imagePanel}>
        <div className={styles.imageParallax} data-about-parallax>
          <img src={portrait} alt="לילך מורד - מתכננת ומעצבת פנים" className={styles.portrait} loading="lazy" />
        </div>
        <div className={styles.imageFade} />
      </div>

      {/* Text content */}
      <div className={styles.content}>
        <div className={styles.textCol}>
          <div data-about-text>
            <h2 className={styles.name}>נעים מאוד, לילך מורד</h2>
            <span className={styles.role}>מתכננת ומעצבת פנים</span>
          </div>

          <div className={styles.spacer} />

          <div className={styles.textBlock} data-about-text>
            <p className={styles.para}>
              15 שנה הייתי אישה של מסגרות בשירות קבע - משמעת וסדר ותכנון של כל צעד מראש. ואז הגיעה חופשת הלידה הראשונה שלי ומשהו השתנה. התחלתי ללמוד עיצוב פנים כתחביב ומצאתי את עצמי מתאהבת בכל שיעור ובכל פרויקט ובכל חומר שנגעתי בו
            </p>
          </div>

          <Divider />

          <div className={styles.textBlock} data-about-text>
            <p className={styles.para}>
              החלטתי לעזוב הכל וללכת אחרי הלב - קפצתי למים העמוקים בלי רשת ביטחון ומאז אני חיה ונושמת את מה שאני עושה. זה הדבר שעושה אותי הכי מאושרת בעולם. הסדר והמשמעת מהצבא לא נעלמו אלא הפכו לכלים שמאפשרים לי לנהל כל פרויקט בצורה מדויקת ומסודרת ובלי הפתעות
            </p>
          </div>

          <Divider />

          <div className={styles.textBlock} data-about-text>
            <p className={styles.para}>
              אני מתייחסת לכל בית כאילו אני עומדת לגור בו בעצמי - לא רק יפה אלא פרקטי וחכם ונכון למשפחה שגרה בו. מהתכנון הראשוני דרך בחירת החומרים ועד הפיקוח בשטח אני שם כדי שתעברו את התהליך בלי טעויות יקרות ובלי חריגות מתקציב ובלי כאבי ראש
            </p>
          </div>


          <div className={styles.ctaWrap} data-about-cta>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={styles.ctaBtn}>
              <span>לחיצה כאן ואנחנו מדברים</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutLilach;
